// client/src/lib/api/auth-api.ts
// ===========================================
// MÓDULO DE AUTENTICACIÓN Y VERIFICACIÓN
// Centraliza login, registro, gestión de sesiones y perfil de Owner.
// Usa apiRequest para inyectar automáticamente headers y manejar errores.
// ===========================================

import type { Shop, Owner } from "@shared/schema";
import { apiRequest, buildUrlWithParams } from "./api-client";
import type { ApiMessageResponse } from "./types";

// -------------------------------------------
// Login y Registro (Shop)
// -------------------------------------------
export async function login(
  username: string,
  password: string,
): Promise<{ shop: Shop; owner?: Owner; otherShops?: Shop[] }> {
  return apiRequest<{ shop: Shop; owner?: Owner; otherShops?: Shop[] }>(
    "/api/auth/login",
    {
      method: "POST",
      body: JSON.stringify({ username, password }),
    },
  );
}

export async function register(shopData: {
  name: string;
  address: string;
  owner: string;
  username: string;
  password: string;
  ownerId?: string;
}): Promise<{ shop: Shop; owner?: Owner }> {
  return apiRequest<{ shop: Shop; owner?: Owner }>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(shopData),
  });
}

// -------------------------------------------
// Login y Registro (Owner)
// -------------------------------------------
export async function loginOwner(
  email: string,
  password: string,
): Promise<{ owner: Owner; shops: Shop[] }> {
  return apiRequest<{ owner: Owner; shops: Shop[] }>("/api/auth/owner/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function registerOwner(ownerData: {
  businessName: string;
  ownerName: string;
  email: string;
  phone?: string;
  password: string;
  planType?: "basic" | "professional" | "enterprise";
}): Promise<{ owner: Owner; shop: Shop }> {
  return apiRequest<{ owner: Owner; shop: Shop }>("/api/auth/owner/register", {
    method: "POST",
    body: JSON.stringify(ownerData),
  });
}

// -------------------------------------------
// Login de Usuarios de Tienda
// -------------------------------------------
// COMENTARIO: Se mantiene en este módulo por ser un endpoint de autenticación.
export async function loginUser(
  username: string,
  password: string,
): Promise<{ user: any; shop: any; owner?: any; token?: string }> {
  return apiRequest<{ user: any; shop: any; owner?: any; token?: string }>(
    "/api/users/login",
    {
      method: "POST",
      body: JSON.stringify({ username, password }),
    },
  );
}

// -------------------------------------------
// Verificación de Email
// -------------------------------------------
export async function resendVerificationEmail(
  email: string,
): Promise<ApiMessageResponse> {
  return apiRequest<ApiMessageResponse>("/api/auth/resend-verification", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function checkEmailVerificationStatus(email: string): Promise<{
  success: boolean;
  verified: boolean;
  message?: string;
}> {
  const url = buildUrlWithParams("/api/auth/verification-status", { email });
  return apiRequest<{ success: boolean; verified: boolean; message?: string }>(
    url,
  );
}

// -------------------------------------------
// Actualización de perfil de Owner
// -------------------------------------------
// COMENTARIO: Agregadas para corregir el error de exportación faltante.
// Usan apiRequest para mantener consistencia con el resto del módulo.
export async function updateOwnerPhone(
  ownerId: string,
  phone: string,
): Promise<{ success: boolean; message: string; phone?: string }> {
  return apiRequest(`/api/owners/${ownerId}/phone`, {
    method: "PATCH",
    body: JSON.stringify({ phone }),
  });
}

export async function updateOwnerPassword(
  ownerId: string,
  passwords: { currentPassword: string; newPassword: string },
): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/owners/${ownerId}/password`, {
    method: "PATCH",
    body: JSON.stringify(passwords),
  });
}

export async function updateOwnerCurrency(
  ownerId: string,
  currency: string,
): Promise<{ success: boolean; message: string; currency?: string }> {
  return apiRequest(`/api/owners/${ownerId}/currency`, {
    method: "PATCH",
    body: JSON.stringify({ currency }),
  });
}

export async function updateOwnerProfile(
  ownerId: string,
  updates: { businessName?: string; ownerName?: string; email?: string },
): Promise<{ success: boolean; owner: any }> {
  return apiRequest(`/api/owners/${ownerId}/profile`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}
